<template>
  <div class="max-w-5xl mx-auto space-y-8">
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="120px" label-position="top">
      <!-- 第一组：会议基本信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2">会议基本信息</h3>
        <div class="space-y-4">
          <el-form-item label="会议名称" prop="conference_name" required>
            <el-input
              v-model="formData.conference_name"
              placeholder="请输入会议名称"
            />
          </el-form-item>
          <el-form-item label="参会人员" prop="attendees" required>
            <el-input
              v-model="formData.attendees"
              placeholder="请输入参会人员姓名，多人用逗号分隔"
            />
          </el-form-item>
          <el-form-item label="会议地址" prop="location" required>
            <el-input
              v-model="formData.location"
              placeholder="请输入会议地点"
            />
          </el-form-item>
          <el-form-item label="主办机构" prop="organizer" required>
            <el-input
              v-model="formData.organizer"
              placeholder="请输入主办机构"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 第二组：会议类型与学科 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">会议类型与学科</h3>
        <div class="space-y-4">
          <el-form-item label="会议类型" prop="conference_type" required>
            <el-select 
              v-model="formData.conference_type" 
              placeholder="请选择会议类型"
              class="custom-select"
              popper-class="custom-select-dropdown"
            >
              <el-option value="1" label="国际学术会议" />
              <el-option value="2" label="全国性学术会议" />
              <el-option value="3" label="区域性学术会议" />
              <el-option value="4" label="行业学术会议" />
              <el-option value="5" label="其他学术会议" />
            </el-select>
          </el-form-item>
          <el-form-item label="学科分类" prop="subject" required>
            <el-input
              v-model="formData.subject"
              placeholder="请输入具体学科名称"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 第三组：时间信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">时间信息</h3>
        <div class="space-y-4">
          <el-form-item label="开始时间" prop="start_date" required>
            <el-date-picker
              v-model="formData.start_date"
              type="date"
              placeholder="选择开始时间"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item label="结束时间" prop="end_date" required>
            <el-date-picker
              v-model="formData.end_date"
              type="date"
              placeholder="选择结束时间"
              style="width: 100%"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 第四组：论文与出版信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">论文与出版信息</h3>
        <div class="space-y-4">
          <RadioOption
            label="是否提交论文"
            :required="true"
            v-model="formData.is_submit_paper"
            :options="[
              { value: '1', label: '是' },
              { value: '0', label: '否' }
            ]"
          />
          <RadioOption
            label="是否出版或发表"
            :required="true"
            v-model="formData.is_publish"
            :options="[
              { value: '1', label: '是' },
              { value: '0', label: '否' }
            ]"
          />
        </div>
      </div>

      <!-- 第五组：其他信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">其他信息</h3>
        <div class="space-y-4">
          <el-form-item label="备注">
            <el-input
              v-model="formData.remark"
              type="textarea"
              :rows="4"
              placeholder="请输入备注信息"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 文件上传 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">组成材料</h3>
        <FileUpload 
          ref="uploadRef"
          @success="onUploadSuccess"
          @change="onUploadChange"
        />
      </div>

      <!-- 提交按钮 -->
      <div class="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <el-button 
          :disabled="loading"
          @click="submitForm(true)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none border bg-white text-gray-900 hover:bg-gray-50 hover:text-gray-900 h-9 px-4 py-2"
        >
          保存草稿
        </el-button>
        <el-button 
          :disabled="loading"
          @click="submitForm(false)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none bg-blue-600 text-white hover:bg-blue-700 h-9 px-4 py-2 border-0"
        >
          提交
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { saveAcademicConference, submitAcademicConference, updateAcademicConference, getAcademicConferenceById } from '../../api/academicConference'
import { uploadFiles } from '../../api/upload'
import FileUpload from '../common/FileUpload.vue'
import RadioOption from '../common/RadioOption.vue'
import { useRoute } from 'vue-router'

// 表单数据
const formData = reactive({
  // 会议基本信息
  conference_name: '',
  attendees: '',
  location: '',
  organizer: '',
  
  // 会议类型与学科
  conference_type: '',
  subject: '',
  
  // 时间信息
  start_date: '',
  end_date: '',
  
  // 论文与出版信息
  is_submit_paper: '',
  is_publish: '',
  
  // 其他信息
  remark: ''
})

const currentId = ref(null)
const loading = ref(false)
const formRef = ref(null)
const uploadRef = ref(null)
// 证据材料（OSS地址，多个用逗号分隔）
const evidence_material = ref('')
// 内部维护列表，便于增删
const evidenceMaterialList = ref([])

const LAST_DRAFT_KEY = 'conference:lastDraftId'
const DRAFT_FORM_KEY = 'conference:draftForm'
const DRAFT_STATUS_KEY = 'conference:draftStatus'

const rules = reactive({
  conference_name: [{ required: true, message: '请输入会议名称', trigger: 'blur' }],
  attendees: [
    { required: true, message: '请输入参会人员', trigger: 'blur' },
    { validator: attendeesValidator, trigger: 'blur' }
  ],
  location: [{ required: true, message: '请输入会议地点', trigger: 'blur' }],
  organizer: [{ required: true, message: '请输入主办机构', trigger: 'blur' }],
  conference_type: [{ required: true, message: '请选择会议类型', trigger: 'change' }],
  subject: [
    { required: true, message: '请输入学科分类', trigger: 'blur' },
    { validator: subjectCategoryValidator, trigger: 'blur' }
  ],
  start_date: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
  end_date: [
    { required: true, message: '请选择结束时间', trigger: 'change' },
    { validator: endDateValidator, trigger: 'change' }
  ]
})

function attendeesValidator(rule, value, callback) {
  const raw = value == null ? '' : String(value).trim()
  if (!raw) return callback(new Error('请输入参会人员'))
  const normalized = raw.replace(/，/g, ',')
  const parts = normalized.split(',').map(s => s.trim()).filter(Boolean)
  if (parts.length === 0) return callback(new Error('请按“姓名1,姓名2”格式填写'))
  if (parts.some(n => /^\d+$/.test(n))) return callback(new Error('姓名不能为纯数字'))
  return callback()
}

function subjectCategoryValidator(rule, value, callback) {
  const v = value == null ? '' : String(value).trim()
  if (!v) return callback()
  if (/^\d+$/.test(v)) return callback(new Error('请填写规范的学科分类中文名'))
  return callback()
}

function endDateValidator(rule, value, callback) {
  const start = formData.start_date
  const end = value
  if (!start || !end) return callback()
  const s = typeof start === 'string' ? new Date(start) : start
  const e = typeof end === 'string' ? new Date(end) : end
  if (s instanceof Date && e instanceof Date && !isNaN(s.getTime()) && !isNaN(e.getTime())) {
    if (e.getTime() < s.getTime()) return callback(new Error('结束时间不能早于开始时间'))
  }
  return callback()
}

function normalizeAttendees(v) {
  const raw = String(v || '').trim().replace(/[，、]/g, ',')
  if (!raw) return ''
  return raw.split(',').map(s => s.trim()).filter(Boolean).join(',')
}

function sanitizeEvidenceMaterial(s) {
  const v = String(s || '')
  if (!v) return ''
  return v
    .split(',')
    .map(p => String(p || '').trim().replace(/`/g, ''))
    .filter(Boolean)
    .join(',')
}

function isValidUrl(u) {
  return /^https?:\/\/\S+$/.test(String(u || ''))
}

function filterValidEvidence(s) {
  const cleaned = sanitizeEvidenceMaterial(s)
  if (!cleaned) return ''
  const parts = cleaned.split(',').map(x => x.trim()).filter(Boolean)
  const valid = parts.filter(isValidUrl)
  return valid.join(',')
}

const CONF_TYPE_LABELS = {
  '1': '国际会议',
  '2': '国内会议',
  '3': '区域性学术会议',
  '4': '行业学术会议',
  '5': '其他学术会议'
}

function toConferenceTypeLabel(v) {
  const key = String(v ?? '')
  return CONF_TYPE_LABELS[key] ?? key
}

function toConferenceTypeCode(label) {
  const entries = Object.entries(CONF_TYPE_LABELS)
  for (const [code, text] of entries) {
    if (String(label || '').trim() === text) return code
  }
  const key = String(label ?? '')
  return ['1','2','3','4','5'].includes(key) ? key : ''
}

function inferAchievementTypeFromAttendees(v) {
  const norm = normalizeAttendees(v)
  if (!norm) return 1
  const count = norm.split(',').filter(Boolean).length
  return count > 1 ? 2 : 1
}

// 字段映射函数：前端字段 -> 数据库字段（蛇形命名）
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mapFormToDatabase = (data) => ({
  conference_name: data.conference_name,
  participants: normalizeAttendees(data.attendees),
  conference_location: data.location,
  organizing_institution: data.organizer,
  conference_type: toConferenceTypeLabel(data.conference_type),
  subject_category: data.subject,
  start_time: formatDateTimePlain(data.start_date),
  end_time: formatDateTimePlain(data.end_date),
  is_paper_submitted: parseInt(data.is_submit_paper || 0, 10) || 0,
  is_published: parseInt(data.is_publish || 0, 10) || 0,
  remark: data.remark,
  evidence_material: filterValidEvidence(evidence_material.value || '')
})

// 字段映射函数：前端字段 -> OpenAPI DTO（驼峰命名）
const mapFormToApi = (data, isDraft) => ({
  id: currentId.value || undefined,
  achievementType: inferAchievementTypeFromAttendees(data.attendees),
  conferenceName: data.conference_name,
  participants: normalizeAttendees(data.attendees),
  conferenceLocation: data.location,
  organizingInstitution: data.organizer,
  conferenceType: toConferenceTypeLabel(data.conference_type),
  subjectCategory: data.subject,
  startTime: formatDateTimePlain(data.start_date),
  endTime: formatDateTimePlain(data.end_date),
  isPaperSubmitted: parseInt(data.is_submit_paper || 0, 10) || 0,
  isPublished: parseInt(data.is_publish || 0, 10) || 0,
  remark: data.remark,
  evidenceMaterial: filterValidEvidence(evidence_material.value || ''),
  status: isDraft ? 1 : 2
})

// 字段映射函数：后端 DTO -> 前端字段（回显）
const mapApiToForm = (dto = {}) => {
  formData.conference_name = dto.conferenceName ?? ''
  formData.attendees = dto.participants ?? ''
  formData.location = dto.conferenceLocation ?? ''
  formData.organizer = dto.organizingInstitution ?? ''

  formData.conference_type = toConferenceTypeCode(dto.conferenceType ?? '')
  formData.subject = dto.subjectCategory ?? ''

  formData.start_date = dto.startTime ? new Date(dto.startTime) : ''
  formData.end_date = dto.endTime ? new Date(dto.endTime) : ''

  formData.is_submit_paper = String(dto.isPaperSubmitted ?? '')
  formData.is_publish = String(dto.isPublished ?? '')

  formData.remark = dto.remark ?? ''

  const em = dto.evidenceMaterial || ''
  evidence_material.value = em
  evidenceMaterialList.value = em ? String(em).split(',').filter(Boolean) : []
}

// 时间格式化：Date | string -> 'YYYY-MM-DDTHH:mm:ss'
// 移除未使用的日期纯格式函数，统一使用 formatDateTime

function formatDateTimePlain(input) {
  if (!input) return undefined
  const d = typeof input === 'string' ? new Date(input) : input
  if (!(d instanceof Date) || isNaN(d.getTime())) return undefined
  const pad = (n) => String(n).padStart(2, '0')
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const h = pad(d.getHours())
  const min = pad(d.getMinutes())
  const s = pad(d.getSeconds())
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

// 移除未使用的日期时间格式化函数，统一使用 formatDateOnly

// 删除 formatDateOnly，统一使用 formatDateTime 返回日期字符串

// 鉴权与头部由统一 http 实例处理，组件无需重复实现

// 提交表单
const submitForm = async (isDraft = false) => {
  try {
    loading.value = true
    const ok = await (formRef.value?.validate?.() ?? Promise.resolve(true)).catch(() => false)
    if (!ok) {
      ElMessage.error('请完善所有必填项')
      loading.value = false
      return
    }
    if (!formData.is_submit_paper) {
      ElMessage.error('请选择是否提交论文')
      loading.value = false
      return
    }
    if (!formData.is_publish) {
      ElMessage.error('请选择是否出版或发表')
      loading.value = false
      return
    }
    const base = mapFormToApi(formData, isDraft)
    if (!currentId.value) {
      if (isDraft) {
        const resp = await saveAcademicConference(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      } else {
        const resp = await submitAcademicConference(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      }
    } else {
      const updateDto = { ...base, id: Number(currentId.value), status: isDraft ? 1 : 2 }
      await updateAcademicConference(updateDto)
    }
    if (isDraft) {
      if (!currentId.value) {
        try {
          localStorage.setItem(LAST_DRAFT_KEY, String(currentId.value))
          localStorage.setItem(DRAFT_STATUS_KEY, '1')
          const snapshot = {
            form: { ...formData },
            evidenceMaterial: evidence_material.value || '',
            evidenceMaterialList: Array.isArray(evidenceMaterialList.value) ? [...evidenceMaterialList.value] : [],
            currentId: currentId.value || null
          }
          localStorage.setItem(DRAFT_FORM_KEY, JSON.stringify(snapshot))
        } catch { void 0 }
      }
    } else {
      try { await nextTick(); formRef.value?.clearValidate?.(); formRef.value?.resetFields?.() } catch { void 0 }
      currentId.value = null
      try { localStorage.removeItem(LAST_DRAFT_KEY) } catch { void 0 }
      try { localStorage.removeItem(DRAFT_FORM_KEY) } catch { void 0 }
      try { localStorage.removeItem(DRAFT_STATUS_KEY) } catch { void 0 }
      try { resetForm() } catch { void 0 }
      try { uploadRef.value?.clear?.() } catch { void 0 }
    }
    ElMessage.success(isDraft ? '草稿保存成功' : '提交成功')
  } catch {
    ElMessage.error(isDraft ? '草稿保存失败' : '提交失败')
  } finally {
    loading.value = false
  }
}

// ===== 文件上传到 OSS（复用函数） =====
// 将上传结果写入当前表单的 evidence_material（逗号分隔）
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function setEvidenceMaterialFromFiles(files) {
  try {
    const addrs = await uploadFiles(files)
    evidence_material.value = Array.isArray(addrs) ? addrs.join(',') : ''
  } catch {
    ElMessage.error('文件上传失败')
  }
}

// 上传成功与变更事件处理
function onUploadSuccess({ url }) {
  if (url && !evidenceMaterialList.value.includes(url)) {
    evidenceMaterialList.value.push(url)
    evidence_material.value = evidenceMaterialList.value.join(',')
  }
}

function onUploadChange({ url, type }) {
  if (type === 'remove' && url) {
    evidenceMaterialList.value = evidenceMaterialList.value.filter(u => u !== url)
    evidence_material.value = evidenceMaterialList.value.join(',')
  }
}

// 重置表单
const resetForm = () => {
  Object.assign(formData, {
    conference_name: '',
    attendees: '',
    location: '',
    organizer: '',
    conference_type: '',
    subject: '',
    start_date: '',
    end_date: '',
    is_submit_paper: '',
    is_publish: '',
    remark: ''
  })
  evidence_material.value = ''
  evidenceMaterialList.value = []
  currentId.value = null
}

// —— 通过路由回显加载 ——
const route = useRoute()
onMounted(async () => {
  const rid = route.query?.detailId || route.query?.id  // ← 优先用 detailId
  if (rid) {
    try {
      currentId.value = rid
      const dto = await getAcademicConferenceById(rid)
      mapApiToForm(dto?.data ?? dto)
    } catch (e) {
      console.warn('加载会议详情失败', e)
    }
  } else {
    try {
      const draftFlag = localStorage.getItem(DRAFT_STATUS_KEY)
      if (draftFlag === '1') {
        const raw = localStorage.getItem(DRAFT_FORM_KEY)
        if (raw) {
          try {
            const snapshot = JSON.parse(raw)
            const f = snapshot?.form || {}
            Object.assign(formData, f)
            evidence_material.value = snapshot?.evidenceMaterial || ''
            evidenceMaterialList.value = Array.isArray(snapshot?.evidenceMaterialList) ? [...snapshot.evidenceMaterialList] : []
            const id = snapshot?.currentId || localStorage.getItem(LAST_DRAFT_KEY)
            if (id) currentId.value = id
          } catch { void 0 }
        }
      }
    } catch { void 0 }
  }
})
</script>

<style scoped>
/* 自定义Select样式 */
:deep(.custom-select .el-select__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-select .el-select__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-select .el-select__wrapper.is-focused) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-select .el-select__placeholder) {
  color: #9ca3af;
}

:deep(.custom-select .el-select__input) {
  color: #111827;
  background-color: transparent;
}

/* 下拉框样式 */
:deep(.custom-select-dropdown) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  background-color: white;
}

:deep(.custom-select-dropdown .el-select-dropdown__item) {
  padding: 8px 12px;
  color: #374151;
  transition: all 0.2s ease-in-out;
}

:deep(.custom-select-dropdown .el-select-dropdown__item:hover) {
  background-color: #f3f4f6;
  color: #111827;
}

:deep(.custom-select-dropdown .el-select-dropdown__item.is-selected) {
  background-color: #dbeafe;
  color: #1d4ed8;
  font-weight: 500;
}

/* 自定义Input样式，与Select保持一致 */
:deep(.custom-input .el-input__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-input .el-input__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-input .el-input__wrapper.is-focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-input .el-input__inner) {
  color: #111827;
  background-color: transparent;
}

:deep(.custom-input .el-input__inner::placeholder) {
  color: #9ca3af;
}
</style>